import React from 'react';

import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import {Tag} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeStudiesAssignUnitOperationEventMetadata,
  STUDIES_ASSIGN_UNIT_OPERATION_ENDED_EVENT,
  STUDIES_ASSIGN_UNIT_OPERATION_STARTED_EVENT,
} from 'performance/processStudiesEvents';
import * as ProcessManagementDAO from 'processes/dao/ProcessManagementDAO';
import ProcessTaskResponsiblePartiesEditorModal from 'processes/dashboard/bulk-edit/ProcessTaskResponsiblePartiesEditorModal';
import {doSelectedItemsExceedLimit} from 'processes/dashboard/ProcessManagementSidepanelConstants';
import {isProcessPlatformFeatureEnabledAndHasCapability} from 'processes/processes-platform/FeatureOptions';
import submitTasksWithConfirmation from 'processes/tables/submitTasksWithConfirmation';
import {InventoryRegistryDataModAction} from 'samples/getInventoryRegistryChangeSummary';
import getOptionDataFromTag from 'schemas/tags/tag-editor/getOptionDataFromTag';
import TagEditorModal, {TagEditorModalResult} from 'schemas/tags/tag-editor/TagEditorModal';
import {getTagUpdateParams} from 'schemas/tags/TagUtil';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';

import {ProcessTaskListingGraphQL} from '../../../../graphql-fragments/processes/__generated/ProcessTaskListingGraphQL';
import {
  ActionMethod,
  ActionOrigin,
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from '../../../graphql-client/__generated/graphqlGlobalTypes';
import ProcessTaskAssigneeEditorModal from './ProcessTaskAssigneeEditorModal';
import ProcessTaskScheduledOnEditorModal from './ProcessTaskScheduledOnEditorModal';
import ProcessTaskStatusEditorModal from './ProcessTaskStatusEditorModal';

type ProcessTaskStatus = ProcessTaskListingGraphQL['status'];

enum ProcessTaskEditOptions {
  ASSIGNEE = 'assignee',
  SCHEDULED_ON_DATE = 'scheduledOnDate',
  STATUS = 'status',
  RESPONSIBLE_PARTIES = 'responsibleParties',
}

interface ProcessTaskEditorDropdownProps {
  items: Array<ProcessTaskListingGraphQL>;
  // This callback aims to track item selections.
  onClickTracking?: (selectedItemName: string) => void;
}

interface ProcessTaskEditorDropdownMenuProps extends ProcessTaskEditorDropdownProps {
  areItemsEditable: boolean;
}

async function bulkUpdateProcessTasks(
  items: Array<ProcessTaskListingGraphQL>,
  {
    assigneeId,
    statusId,
    newField,
    scheduledOnDate,
    responsiblePartiesIds,
    dataModsHeader,
  }: {
    assigneeId?: string | null;
    statusId?: string;
    newField?: {name: string; tag: Tag};
    scheduledOnDate?: string | null;
    responsiblePartiesIds?: Array<string>;
    dataModsHeader?: DataModificationsHeader;
  }
): Promise<void> {
  if (
    assigneeId &&
    items.some((item) => item.schema.executionType === ProcessExecutionType.PROCEDURE_METHOD)
  ) {
    logProfilerTimingEvent(
      STUDIES_ASSIGN_UNIT_OPERATION_STARTED_EVENT,
      composeStudiesAssignUnitOperationEventMetadata(ActionOrigin.WORKFLOWS_DASHBOARD)
    );
  }
  try {
    const tasks = items.map((item) => ({
      id: item.id,
      sourceType: 'dashboard',
      actionOrigin: ActionOrigin.WORKFLOWS_DASHBOARD,
      actionMethod: ActionMethod.BUTTON_CLICK,
      ...(newField ? {fields: {[newField.name]: getTagUpdateParams(newField.tag)}} : {}),
      ...(assigneeId !== undefined ? {assigneeId} : {}),
      ...(statusId != null ? {statusId} : {}),
      ...(scheduledOnDate !== undefined ? {scheduledOnDate} : {}),
      ...(responsiblePartiesIds !== undefined ? {responsiblePartiesIds} : {}),
    }));
    const updateFn = async (): Promise<void> => {
      await ProcessManagementDAO.bulkCreateOrUpdateProcessTasks({
        tasks,
        dataModsHeader,
      });
      AppDispatcher.dispatch({
        actionType: EventConstants.PROCESS_MANAGEMENT_ITEMS_UPDATED,
        itemIds: items.map(({id}) => id),
      });
      notifications.success(`Successfully updated ${pluralize(items.length, 'task')}`);
      logProfilerTimingEvent(
        STUDIES_ASSIGN_UNIT_OPERATION_ENDED_EVENT,
        composeStudiesAssignUnitOperationEventMetadata(ActionOrigin.WORKFLOWS_DASHBOARD)
      );
    };
    if (newField != null || statusId != null) {
      await submitTasksWithConfirmation({
        taskSchemaId: items[0]?.schema.id,
        tasks: tasks.map((task) => ({
          ...task,
          statusId,
          oldStatusId: task.statusId!,
        })),
        onRequestSubmit: async () => {
          await updateFn();
        },
      });
    } else {
      await updateFn();
    }

    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    notifications.notifyError(e);
  }
}

function includesFlowchartTasks(tasks: Array<ProcessTaskListingGraphQL>): boolean {
  return (
    tasks.length > 0 && tasks.some((task) => task.schema.executionType === ProcessExecutionType.FLOWCHART)
  );
}

function getAssigneeOption(props: ProcessTaskEditorDropdownMenuProps): Array<OptionData> {
  const isFlowchartTaskSelected = includesFlowchartTasks(props.items);
  return [
    {
      value: ProcessTaskEditOptions.ASSIGNEE,
      name: 'Assignee',
      disabled: !props.areItemsEditable || isFlowchartTaskSelected,
      disabledMessage: isFlowchartTaskSelected ? 'Flowchart tasks cannot be assigned' : undefined,
    },
  ];
}

function getResponsiblePartiesOption(props: ProcessTaskEditorDropdownMenuProps): Array<OptionData> {
  if (!BenchlingConfig.WORKFLOWS_ENABLE_COLLABORATORS) {
    return [];
  }
  const {areItemsEditable} = props;
  return [
    {
      value: ProcessTaskEditOptions.RESPONSIBLE_PARTIES,
      name: 'Responsible parties',
      disabled: !areItemsEditable,
      disabledMessage: undefined,
    },
  ];
}

function getStatusOption(props: ProcessTaskEditorDropdownMenuProps): Array<OptionData> {
  const canEditStatus =
    props.areItemsEditable &&
    props.items.every(
      (item) =>
        item.schema.statusLifecycle.id === props.items[0].schema.statusLifecycle.id &&
        item.status.id === props.items[0].status.id
    );
  return [
    {
      value: ProcessTaskEditOptions.STATUS,
      name: 'Status',
      disabled: !canEditStatus,
      disabledMessage:
        'To edit the status of multiple tasks, the tasks must have the same status and be part of the same status lifecycle',
    },
  ];
}

function getScheduledOnOption(props: ProcessTaskEditorDropdownMenuProps): Array<OptionData> {
  const isFlowchartTaskSelected = includesFlowchartTasks(props.items);
  return [
    {
      value: ProcessTaskEditOptions.SCHEDULED_ON_DATE,
      name: 'Scheduled on',
      disabled: !props.areItemsEditable || isFlowchartTaskSelected,
      disabledMessage: isFlowchartTaskSelected ? 'Flowchart tasks cannot be scheduled' : undefined,
    },
  ];
}

function getFieldsOptions(props: ProcessTaskEditorDropdownMenuProps): Array<OptionData> {
  const canEditSchemaFields =
    props.areItemsEditable && props.items.every((item) => item.schema.id === props.items[0].schema.id);

  const items = props.items.map((item) => ({...item, tags: item.fields}));
  const tagNamesAndTypes = canEditSchemaFields ? FileBrowserUtil.tagTypesByName(items) : {};

  // eslint-disable-next-line no-restricted-syntax
  let options = Object.keys(tagNamesAndTypes)
    .sort()
    .map((name) => getOptionDataFromTag(tagNamesAndTypes, name));
  if (options.length === 0) {
    options = [
      {
        value: 'Items have no fields',
        name: 'Items have no fields',
        disabled: true,
      },
    ];
  }
  return [
    {
      name: 'Field',
      value: null,
      disabled: !canEditSchemaFields,
      disabledMessage: 'Cannot edit fields for tasks of different schemas',
      submenu: [options],
    },
  ];
}

export async function asyncBulkUpdateProcessWithDataMods<ResponseData>(
  itemIds: Array<string>,
  performAction: (dataModsHeader?: DataModificationsHeader) => Promise<void> | void,
  actionOrigin: ActionOrigin,
  changeSummary: string = ''
): Promise<void> {
  if (itemIds.length > 0) {
    await callAsyncWithDataModifications({
      changeSummary,
      onPerformAction: performAction,
      apiIds: itemIds,
      analyticsParams: {
        module: 'WORKFLOWS',
        actionOrigin,
        modifiedAction: InventoryRegistryDataModAction.MODIFY,
        numModifiedTotalItems: itemIds.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_WORKFLOWS,
    });
  } else {
    // If there are no apiIds (no non-status changes to existing outputs or tasks) then directly call performAction
    await performAction();
  }
}

export default function ProcessTaskEditorDropdown(props: ProcessTaskEditorDropdownProps): JSX.Element | null {
  const isPlatformFeatureEnabledForAllItems = (platformFeature: ProcessPlatformFeatureType): boolean =>
    props.items.length > 0 &&
    props.items.every((item) =>
      isProcessPlatformFeatureEnabledAndHasCapability(
        platformFeature,
        item.schema.executionType,
        item.schema.clientOrigin,
        item.schema.name
      )
    );
  const areSystemFieldsEditable =
    props.items.length > 0 && props.items.every((item) => item.permissions.writable);
  const areSchemaFieldsEditable =
    props.items.length > 0 && props.items.every((item) => item.permissions.tagsEditable);

  const runAssigneeEditorModal = async (): Promise<void> => {
    const modalProps = {
      items: props.items,
      onRequestSubmit: (assignee: EntityWithTextAndSuspended) => {
        bulkUpdateProcessTasks(props.items, {assigneeId: assignee?.id ?? null});
      },
    };
    await runModal(ProcessTaskAssigneeEditorModal, modalProps);
  };

  const runScheduledOnEditorModal = async (): Promise<void> => {
    await runModal(ProcessTaskScheduledOnEditorModal, {
      items: props.items,
      onRequestSubmit: (newDate) => {
        bulkUpdateProcessTasks(props.items, {scheduledOnDate: newDate});
      },
    });
  };

  const runResponsiblePartiesEditorModal = async (): Promise<void> => {
    await runModal(ProcessTaskResponsiblePartiesEditorModal, {
      items: props.items,
      onRequestSubmit: (responsibleParties: Array<EntityWithTextAndSuspended>) => {
        bulkUpdateProcessTasks(props.items, {
          responsiblePartiesIds: responsibleParties.map((responsibleParty) => responsibleParty.id),
        });
      },
    });
  };

  const runStatusEditorModal = async (): Promise<void> => {
    const modalProps = {
      processTaskStatusLifecycleId: props.items[0].schema.statusLifecycle.id,
      processTaskStatus: props.items[0].status,
    };
    const newStatus: ProcessTaskStatus | null = await runModal(ProcessTaskStatusEditorModal, modalProps);
    if (newStatus == null) {
      return;
    }
    await bulkUpdateProcessTasks(props.items, {statusId: newStatus.id});
  };

  const runTagEditorModal = async (tagName: string): Promise<void> => {
    const items = props.items.map((item) => ({...item, tags: item.fields}));
    const modalProps = {tagName, selectedItems: items};
    const tagModalResult: TagEditorModalResult | null = await runModal(TagEditorModal, modalProps);
    if (tagModalResult != null) {
      const itemIds = items.map((item) => item.id);
      const performAction = async (dataModsHeader?: DataModificationsHeader): Promise<void> => {
        await bulkUpdateProcessTasks(props.items, {
          newField: {name: tagName, tag: tagModalResult.newTag},
          dataModsHeader,
        });
      };
      await asyncBulkUpdateProcessWithDataMods(itemIds, performAction, ActionOrigin.WORKFLOWS_DASHBOARD);
    }
  };

  const runUpdateProcessTaskModal = async (selectedValue: string): Promise<void> => {
    props.onClickTracking?.(selectedValue);
    if (selectedValue === ProcessTaskEditOptions.ASSIGNEE) {
      runAssigneeEditorModal();
    } else if (selectedValue === ProcessTaskEditOptions.SCHEDULED_ON_DATE) {
      runScheduledOnEditorModal();
    } else if (selectedValue === ProcessTaskEditOptions.RESPONSIBLE_PARTIES) {
      runResponsiblePartiesEditorModal();
    } else if (selectedValue === ProcessTaskEditOptions.STATUS) {
      runStatusEditorModal();
    } else if (selectedValue != null) {
      await runTagEditorModal(selectedValue);
    }
  };

  const optionDataGenerators = [
    {
      platformFeature: ProcessPlatformFeatureType.CAN_EDIT_STATUS_FROM_UI,
      getOptions: getStatusOption,
      areItemsEditable: areSystemFieldsEditable,
    },
    {
      platformFeature: ProcessPlatformFeatureType.CAN_EDIT_ASSIGNEE,
      getOptions: getAssigneeOption,
      areItemsEditable: areSystemFieldsEditable,
    },
    {
      platformFeature: ProcessPlatformFeatureType.CAN_EDIT_SCHEDULED_ON_DATE,
      getOptions: getScheduledOnOption,
      areItemsEditable: areSystemFieldsEditable,
    },
    {
      platformFeature: ProcessPlatformFeatureType.CAN_EDIT_RESPONSIBLE_PARTIES,
      getOptions: getResponsiblePartiesOption,
      areItemsEditable: areSystemFieldsEditable,
    },
    {
      platformFeature: ProcessPlatformFeatureType.CAN_EDIT_SCHEMA_FIELDS,
      getOptions: getFieldsOptions,
      areItemsEditable: areSchemaFieldsEditable,
    },
  ];

  const optionDatas: Array<OptionData> = [];
  for (const {platformFeature, getOptions, areItemsEditable} of optionDataGenerators) {
    optionDatas.push(
      ...(isPlatformFeatureEnabledForAllItems(platformFeature)
        ? getOptions({...props, areItemsEditable})
        : [])
    );
  }

  const {isExceedingLimit, isExceedingLimitMessage} = doSelectedItemsExceedLimit(props.items);

  return optionDatas.length > 0 ? (
    <div className="buttonGroup">
      <Tooltip title={isExceedingLimitMessage ?? 'Edit'} placement={TooltipPlacement.TOP}>
        <Dropdown
          menu={
            <SearchableMenu optionDatas={optionDatas} onSelect={runUpdateProcessTaskModal} dropLeft={true} />
          }
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_WHITE}
            disabled={
              isExceedingLimit ||
              optionDatas.length === 0 ||
              (!areSystemFieldsEditable && !areSchemaFieldsEditable)
            }
            icon="edit"
            isDropdown={true}
            size={ButtonSize.SMALL}
            dataTestAttrs={{component: 'ProcessTaskEditorDropdown', element: 'editButton'}}
          />
        </Dropdown>
      </Tooltip>
    </div>
  ) : null;
}
